import { useQuery, useMutation, useQueryClient, keepPreviousData, type QueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { showToast } from '../lib/toast';
import { ApiRequestError, getApiErrorMessage } from '../lib/apiErrors';
import { invalidateQueries, queryKeys } from '../lib/queryKeys';
import type {
  CreateArticleRequest,
  UpdateArticleRequest,
  ArticleFilterRequest,
  ArticleSearchRequest,
  PublishArticleRequest,
  BulkStatusUpdateRequest,
  BulkDeleteRequest,
  MoveToCollectionRequest,
  SaveArticleDraftRequest,
  PublishArticleDraftRequest,
} from '../types/articles';

export const ARTICLES_QUERY_KEY = 'articles';

/**
 * A change to an article can show anywhere it's listed: every article list and search, its detail, the overview
 * stats, the collections' article counts (the collection delete dialog relies on them) and the ticket link panels that
 * name it (title, status, visibility)
 */
function invalidateAfterArticleChange(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: [ARTICLES_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: queryKeys.articleCollections.all });
  queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes('articleLinks') });
}

// Get articles with filters
export function useArticles(filter?: ArticleFilterRequest) {
  return useQuery({
    queryKey: [ARTICLES_QUERY_KEY, 'list', filter],
    queryFn: async () => {
      const response = await apiService.getArticles(filter);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch articles'));
      }
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    placeholderData: keepPreviousData,
  });
}

// Get single article
export function useArticle(id: string | undefined) {
  return useQuery({
    queryKey: [ARTICLES_QUERY_KEY, 'detail', id],
    queryFn: async () => {
      if (!id) throw new Error('Article ID is required');
      
      const response = await apiService.getArticle(id);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch article'));
      }
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Create article
export function useCreateArticle() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (article: CreateArticleRequest) => {
      const response = await apiService.createArticle(article);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to create article'));
      }
      return response.data;
    },
    onSuccess: (data) => {
      invalidateAfterArticleChange(queryClient);
      showToast.success('Success', `Article "${data?.title}" created successfully`);
    },
    onError: (error: Error) => {
      showToast.error('Creation Failed', error.message);
    },
  });
}

// Update article
export function useUpdateArticle() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateArticleRequest }) => {
      const response = await apiService.updateArticle(id, updates);
      if (!response.success) {
        // Keeps the status: 409 means someone else changed the article since the editor opened it
        throw new ApiRequestError(getApiErrorMessage(response, 'Failed to update article'), response.statusCode);
      }
      return response.data;
    },
    onSuccess: (data) => {
      invalidateAfterArticleChange(queryClient);
      showToast.success('Success', `Article "${data?.title}" updated successfully`);
    },
    onError: (error: Error) => {
      showToast.error('Update Failed', error.message);
    },
  });
}

// Delete article
export function useDeleteArticle() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiService.deleteArticle(id);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to delete article'));
      }
      return response.data;
    },
    onSuccess: () => {
      invalidateAfterArticleChange(queryClient);
      // Permanent delete unlinks the article from tickets
      queryClient.invalidateQueries({ queryKey: invalidateQueries.tickets.all() });
      showToast.success('Success', 'Article permanently deleted');
    },
    onError: (error: Error) => {
      showToast.error('Deletion Failed', error.message);
    },
  });
}

// Search articles
export function useSearchArticles(request?: ArticleSearchRequest, enabled: boolean = false) {
  return useQuery({
    queryKey: [ARTICLES_QUERY_KEY, 'search', request],
    queryFn: async () => {
      if (!request) throw new Error('Search request is required');
      
      const response = await apiService.searchArticles(request);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Search failed'));
      }
      return response.data;
    },
    enabled: enabled && !!request,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

// Get published articles
export function usePublishedArticles(page: number = 1, pageSize: number = 20) {
  return useQuery({
    queryKey: [ARTICLES_QUERY_KEY, 'published', page, pageSize],
    queryFn: async () => {
      const response = await apiService.getPublishedArticles(page, pageSize);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch published articles'));
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Get featured articles
export function useFeaturedArticles() {
  return useQuery({
    queryKey: [ARTICLES_QUERY_KEY, 'featured'],
    queryFn: async () => {
      const response = await apiService.getFeaturedArticles();
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch featured articles'));
      }
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

// Get the articles filed in a collection (and, by default, its sections) in reading order
export function useArticlesByCollection(collectionId: string | undefined, publishedOnly: boolean = true, includeSections: boolean = true) {
  return useQuery({
    queryKey: [ARTICLES_QUERY_KEY, 'collection', collectionId, publishedOnly, includeSections],
    queryFn: async () => {
      if (!collectionId) throw new Error('Collection ID is required');

      const response = await apiService.getArticlesByCollection(collectionId, publishedOnly, includeSections);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch articles by collection'));
      }
      return response.data;
    },
    enabled: !!collectionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Get articles by author
export function useArticlesByAuthor(authorId: string | undefined) {
  return useQuery({
    queryKey: [ARTICLES_QUERY_KEY, 'author', authorId],
    queryFn: async () => {
      if (!authorId) throw new Error('Author ID is required');
      
      const response = await apiService.getArticlesByAuthor(authorId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch articles by author'));
      }
      return response.data;
    },
    enabled: !!authorId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Publish article
export function usePublishArticle() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, request }: { id: string; request: PublishArticleRequest }) => {
      const response = await apiService.publishArticle(id, request);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to publish article'));
      }
      return response.data;
    },
    onSuccess: (data) => {
      invalidateAfterArticleChange(queryClient);
      showToast.success('Success', `Article "${data?.title}" published successfully`);
    },
    onError: (error: Error) => {
      showToast.error('Publish Failed', error.message);
    },
  });
}

// Unpublish article
export function useUnpublishArticle() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiService.unpublishArticle(id);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to unpublish article'));
      }
      return response.data;
    },
    onSuccess: (data) => {
      invalidateAfterArticleChange(queryClient);
      showToast.success('Success', `Article "${data?.title}" unpublished successfully`);
    },
    onError: (error: Error) => {
      showToast.error('Unpublish Failed', error.message);
    },
  });
}

// Archive article
export function useArchiveArticle() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiService.archiveArticle(id);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to archive article'));
      }
      return response.data;
    },
    onSuccess: (data) => {
      invalidateAfterArticleChange(queryClient);
      showToast.success('Success', `Article "${data?.title}" archived successfully`);
    },
    onError: (error: Error) => {
      showToast.error('Archive Failed', error.message);
    },
  });
}

// Restore article
export function useRestoreArticle() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiService.restoreArticle(id);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to restore article'));
      }
      return response.data;
    },
    onSuccess: (data) => {
      invalidateAfterArticleChange(queryClient);
      showToast.success('Success', `Article "${data?.title}" restored successfully`);
    },
    onError: (error: Error) => {
      showToast.error('Restore Failed', error.message);
    },
  });
}


// Record article view
export function useRecordArticleView() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiService.recordArticleView(id);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to record article view'));
      }
      return response.data;
    },
    // Don't show toasts for view recording
    onSuccess: () => {
      // Invalidate stats and most-viewed queries when view is recorded
      queryClient.invalidateQueries({ queryKey: [ARTICLES_QUERY_KEY, 'stats'] });
      queryClient.invalidateQueries({ queryKey: [ARTICLES_QUERY_KEY, 'most-viewed'] });
      queryClient.invalidateQueries({ queryKey: [ARTICLES_QUERY_KEY, 'recently-viewed'] });
    },
  });
}

// Get article statistics
export function useArticleStats(organizationIds?: string[]) {
  return useQuery({
    queryKey: [ARTICLES_QUERY_KEY, 'stats', organizationIds],
    queryFn: async () => {
      const response = await apiService.getArticleStats(organizationIds);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch article statistics'));
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Get most viewed articles
export function useMostViewedArticles(count: number = 10, since?: string, organizationIds?: string[]) {
  return useQuery({
    queryKey: [ARTICLES_QUERY_KEY, 'most-viewed', count, since, organizationIds],
    queryFn: async () => {
      const response = await apiService.getMostViewedArticles(count, since, organizationIds);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch most viewed articles'));
      }
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// Get recently viewed articles
export function useRecentlyViewedArticles(count: number = 10, organizationIds?: string[]) {
  return useQuery({
    queryKey: [ARTICLES_QUERY_KEY, 'recently-viewed', count, organizationIds],
    queryFn: async () => {
      const response = await apiService.getRecentlyViewedArticles(count, organizationIds);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch recently viewed articles'));
      }
      return response.data;
    },
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

// Full text search
export function useFullTextSearch(query: string, publishedOnly: boolean = true, page: number = 1, pageSize: number = 20, enabled: boolean = false) {
  return useQuery({
    queryKey: [ARTICLES_QUERY_KEY, 'full-text-search', query, publishedOnly, page, pageSize],
    queryFn: async () => {
      const response = await apiService.fullTextSearch(query, publishedOnly, page, pageSize);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Search failed'));
      }
      return response.data;
    },
    enabled: enabled && query.length > 0,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

// Get search suggestions
export function useSearchSuggestions(query: string, maxSuggestions: number = 5, enabled: boolean = false) {
  return useQuery({
    queryKey: [ARTICLES_QUERY_KEY, 'search-suggestions', query, maxSuggestions],
    queryFn: async () => {
      const response = await apiService.getSearchSuggestions(query, maxSuggestions);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to get search suggestions'));
      }
      return response.data;
    },
    enabled: enabled && query.length >= 2,
    staleTime: 30 * 1000, // 30 seconds
  });
}

// Bulk operations
export function useBulkUpdateArticleStatus() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (request: BulkStatusUpdateRequest) => {
      const response = await apiService.bulkUpdateArticleStatus(request);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to update article status'));
      }
      return response.data;
    },
    onSuccess: (data) => {
      invalidateAfterArticleChange(queryClient);
      showToast.success('Success', data?.message);
    },
    onError: (error: Error) => {
      showToast.error('Bulk Update Failed', error.message);
    },
  });
}

export function useBulkDeleteArticles() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (request: BulkDeleteRequest) => {
      const response = await apiService.bulkDeleteArticles(request);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to delete articles'));
      }
      return response.data;
    },
    onSuccess: (data) => {
      invalidateAfterArticleChange(queryClient);
      showToast.success('Success', data?.message);
    },
    onError: (error: Error) => {
      showToast.error('Bulk Delete Failed', error.message);
    },
  });
}

// File articles in a collection, or leave them uncategorized (collectionId null)
export function useMoveArticlesToCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: MoveToCollectionRequest) => {
      const response = await apiService.moveArticlesToCollection(request);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to move articles'));
      }
      return response.data;
    },
    onSuccess: (data) => {
      invalidateAfterArticleChange(queryClient);
      showToast.success('Articles Moved', `${data?.movedCount ?? 0} article(s) moved.`);
    },
    onError: (error: Error) => {
      showToast.error('Move Failed', error.message);
    },
  });
}

// Pass the article's organizationId: without it the API falls back to the user's first organization,
// so feedback on articles in the user's other organizations comes back empty.
export function useArticleFeedbackSummary(articleId: string | undefined, organizationId?: string) {
  return useQuery({
    queryKey: ['articles', 'feedback', 'summary', articleId, organizationId],
    queryFn: async () => {
      const response = await apiService.getArticleFeedbackSummary(articleId!, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch feedback summary'));
      }
      return response.data;
    },
    enabled: !!articleId,
    staleTime: 60000,
  });
}

export function useArticleFeedbackList(articleId: string | undefined, page: number = 1, pageSize: number = 20, organizationId?: string) {
  return useQuery({
    queryKey: ['articles', 'feedback', 'list', articleId, page, pageSize, organizationId],
    queryFn: async () => {
      const response = await apiService.getArticleFeedbackList(articleId!, page, pageSize, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch feedback'));
      }
      return response.data;
    },
    enabled: !!articleId,
    staleTime: 30000,
    // Paging through feedback keeps the current page on screen until the next arrives
    placeholderData: keepPreviousData,
  });
}

export function useBulkUpdateArticleSortOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ items, organizationId }: { items: { articleId: string; sortOrder: number }[]; organizationId?: string }) => {
      const response = await apiService.bulkUpdateArticleSortOrder(items, organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to update sort order'));
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ARTICLES_QUERY_KEY] });
    },
  });
}
// ── Authoring: revisions, drafts of published articles, scheduling, reviews ──

// The article's saved versions, newest first
export function useArticleRevisions(articleId: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.articles.revisions(articleId ?? ''),
    queryFn: async () => {
      const response = await apiService.getArticleRevisions(articleId!);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch revision history'));
      }
      return response.data;
    },
    enabled: !!articleId && enabled,
  });
}

export function useArticleRevision(articleId: string | undefined, revisionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.articles.revision(articleId ?? '', revisionId ?? ''),
    queryFn: async () => {
      const response = await apiService.getArticleRevision(articleId!, revisionId!);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch revision'));
      }
      return response.data;
    },
    enabled: !!articleId && !!revisionId,
    // A revision never changes
    staleTime: Infinity,
  });
}

export function useRestoreArticleRevision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ articleId, revisionId }: { articleId: string; revisionId: string }) => {
      const response = await apiService.restoreArticleRevision(articleId, revisionId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to restore revision'));
      }
      return response.data;
    },
    onSuccess: (data) => {
      invalidateAfterArticleChange(queryClient);
      showToast.success(
        'Revision Restored',
        data.hasDraft
          ? 'The earlier version is now the unpublished changes - review it and publish when ready.'
          : 'The article now has the earlier version\'s text.'
      );
    },
    onError: (error: Error) => {
      showToast.error('Restore Failed', error.message);
    },
  });
}

/**
 * A published article's unpublished changes - only asked for when the article says it has some. Keyed by the
 * draftUpdatedAt the article reports, so each saved version is fetched fresh: a cached earlier version (already
 * published, discarded or replaced) is never shown, previewed or published as the current one.
 */
export function useArticleDraft(articleId: string | undefined, draftUpdatedAt: string | null | undefined) {
  return useQuery({
    queryKey: [...queryKeys.articles.draft(articleId ?? ''), draftUpdatedAt ?? null],
    queryFn: async () => {
      const response = await apiService.getArticleDraft(articleId!);
      if (!response.success || !response.data) {
        throw new ApiRequestError(getApiErrorMessage(response, 'Failed to fetch unpublished changes'), response.statusCode);
      }
      return response.data;
    },
    enabled: !!articleId && !!draftUpdatedAt,
    // Gone (published or discarded meanwhile) isn't worth retrying - the article's next refetch says so
    retry: (failureCount, error) => !(error instanceof ApiRequestError && error.status === 404) && failureCount < 3,
  });
}

/**
 * Saves a published article's changes without publishing them. Throws ApiRequestError keeping the status: 409 means
 * the draft (or the article, for a new draft) changed since the editor loaded it. No toast - the editor says what
 * happened, since saving is one step of "Publish changes" too.
 */
export function useSaveArticleDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ articleId, request }: { articleId: string; request: SaveArticleDraftRequest }) => {
      const response = await apiService.saveArticleDraft(articleId, request);
      if (!response.success || !response.data) {
        throw new ApiRequestError(getApiErrorMessage(response, 'Failed to save the draft'), response.statusCode);
      }
      return response.data;
    },
    onSuccess: () => {
      invalidateAfterArticleChange(queryClient);
    },
  });
}

export function usePublishArticleDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ articleId, request }: { articleId: string; request: PublishArticleDraftRequest }) => {
      const response = await apiService.publishArticleDraft(articleId, request);
      if (!response.success || !response.data) {
        throw new ApiRequestError(getApiErrorMessage(response, 'Failed to publish the changes'), response.statusCode);
      }
      return response.data;
    },
    onSuccess: () => {
      invalidateAfterArticleChange(queryClient);
    },
  });
}

export function useDiscardArticleDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (articleId: string) => {
      const response = await apiService.discardArticleDraft(articleId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to discard the changes'));
      }
      return response.data;
    },
    onSuccess: () => {
      invalidateAfterArticleChange(queryClient);
      showToast.success('Changes Discarded', 'The unpublished changes were discarded. Readers see the published article.');
    },
    onError: (error: Error) => {
      showToast.error('Discard Failed', error.message);
    },
  });
}

export function useScheduleArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ articleId, publishAt }: { articleId: string; publishAt: string }) => {
      const response = await apiService.scheduleArticle(articleId, publishAt);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to schedule the article'));
      }
      return response.data;
    },
    onSuccess: () => {
      invalidateAfterArticleChange(queryClient);
      showToast.success('Article Scheduled', 'It will be published at the time you chose.');
    },
    onError: (error: Error) => {
      showToast.error('Scheduling Failed', error.message);
    },
  });
}

export function useCancelArticleSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (articleId: string) => {
      const response = await apiService.cancelArticleSchedule(articleId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to cancel the schedule'));
      }
      return response.data;
    },
    onSuccess: () => {
      invalidateAfterArticleChange(queryClient);
      showToast.success('Schedule Cancelled', 'The article stays a draft.');
    },
    onError: (error: Error) => {
      showToast.error('Cancel Failed', error.message);
    },
  });
}

export function useMarkArticleReviewed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (articleId: string) => {
      const response = await apiService.markArticleReviewed(articleId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to mark the article as reviewed'));
      }
      return response.data;
    },
    onSuccess: () => {
      invalidateAfterArticleChange(queryClient);
      showToast.success('Marked as Reviewed', 'The article is off the review list until it is due again.');
    },
    onError: (error: Error) => {
      showToast.error('Review Failed', error.message);
    },
  });
}

// Published articles that need someone to look at them (unhelpful, due for review, not read lately)
export function useArticlesNeedingAttention(organizationIds?: string[]) {
  return useQuery({
    queryKey: queryKeys.articles.needsAttention(organizationIds),
    queryFn: async () => {
      const response = await apiService.getArticlesNeedingAttention(organizationIds);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch articles needing attention'));
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// Sets the reading order of the articles filed directly in a collection or section (Admin in its organization)
export function useReorderCollectionArticles() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ collectionId, orderedIds, organizationId }: { collectionId: string; orderedIds: string[]; organizationId?: string }) => {
      const response = await apiService.reorderCollectionArticles(collectionId, orderedIds, organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to reorder the articles'));
      }
      return response.data;
    },
    onError: (error: Error) => {
      showToast.error('Reorder Failed', error.message);
    },
    // Refetch either way, so an order the API refused as stale snaps back to the real one
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [ARTICLES_QUERY_KEY] });
    },
  });
}

// What customers searched for in the portal over the last `days` - top searches and the ones that found nothing
export function useSearchInsights(organizationIds: string[] | undefined, days: number) {
  return useQuery({
    queryKey: queryKeys.articles.searchInsights(organizationIds, days),
    queryFn: async () => {
      const response = await apiService.getSearchInsights(organizationIds, days);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch search insights'));
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    // Switching the period keeps the last figures up until the new ones arrive
    placeholderData: keepPreviousData,
  });
}
